import * as stylex from '@stylexjs/stylex';
import { useForm, useStore } from '@tanstack/react-form';
import { useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { allCountries } from 'country-region-data';
import { useId } from 'react';
import {
  DialogTrigger,
  FieldErrorContext,
  Group,
  ListLayout,
  RadioGroup,
  Virtualizer,
  type Key
} from 'react-aria-components';
import { pickBy as rPickBy, keys as rKeys, omit as rOmit } from 'remeda';
import { safeParse as vSafeParse } from 'valibot';

import { ChangePasswordDialog } from '@/components/change-password-dialog.component';
import { DeleteAccountDialog } from '@/components/delete-account-dialog.component';
import { InterestsDialog } from '@/components/interests-dialog.component';
import { DataRequestStatus, NewsletterCadence, NonUSDoctorSpecialtyEnum, Theme } from '@/libs/openapi';
import {
  Button,
  ComboBox,
  Heading,
  Hint,
  Input,
  Label,
  ListBox,
  ListBoxItem,
  Paragraph,
  Popover,
  Radio,
  Text,
  TextButton,
  TextField,
  EditStrokeIcon,
  TEXT_BUTTON_SLOTS,
  Icon,
  SegmentedButtonGroup,
  SegmentedButton,
  TOAST_TIMEOUT,
  useMediaQuery,
  View,
  SKELETON_MIN_DELAY_DURATION,
  LIST_BOX_ITEM_SLOTS,
  minDelay,
  PROGRESS_INDICATOR_MIN_DELAY_DURATION,
  LinkTextButton,
  useDebounce,
  useLocalStorage,
  ProgressIndicator,
} from '@/libs/serenity-ui';
import { breakpoints } from '@/libs/serenity-ui/constants.stylex';
import { $ } from '@/libs/serenity-ui/tokens.stylex';
import { RouterLink } from '@/libs/tanstack-router';
import { RouterTextButtonLink } from '@/libs/tanstack-router/components/router-text-button-link.component';
import { locationQueries } from '@/resources/location/location.api';
import { profileMutations, profileQueries } from '@/resources/profile/profile.api';
import { ProfileSchema } from '@/resources/profile/profile.schema';
import { specialtyQuery } from '@/resources/specialty/specialty.api';
import { userMutations, userQueries } from '@/resources/user/user.api';
import { tryCatch } from '@/utils/try-catch.util';

export const Route = createFileRoute('/_app/settings')({
  component: RouteComponent,
  staticData: {
    contextBarComponent: ContextBarComponent,
  },
  loader: ({ context }) => context.queryClient.ensureQueryData(profileQueries.get),
  head: () => ({
    meta: [
      {
        title: 'TrendMD | Settings',
      },
    ],
  })
});

function RouteComponent() {
  const id = useId();

  const { toastQueue } = Route.useRouteContext();

  const isMobile = useMediaQuery(breakpoints.small);

  const [theme, setTheme] = useLocalStorage('theme', Theme.SYSTEM);

  const profileQuery = useSuspenseQuery(profileQueries.get);
  const userQuery = useQuery(userQueries.me);
  const dataRequestDetailQuery = useQuery(userQueries.dataRequestDetail);
  const interestsQuery = useQuery(userQueries.interests);

  const updateProfileMutation = useMutation(profileMutations.update);
  const recoverPasswordMutation = useMutation(userMutations.recoverPassword);
  const requestDataMutation = useMutation(userMutations.requestData);
  const updateUserInterestsMutation = useMutation(userMutations.updateInterests);

  const autoSaveForm = useForm({
    defaultValues: {
      full_name: profileQuery.data?.full_name ?? '',
      newsletter: profileQuery.data?.newsletter ?? false,
      cookies: profileQuery.isLoading ? null : profileQuery.data?.cookies ? "accept-all" : "refuse-non-essential",
      cadence: profileQuery.data?.cadence ?? NewsletterCadence.Value1,
    },
    canSubmitWhenInvalid: true,
    onSubmit: ({ value }) => {
      updateProfileMutation.mutate({
        full_name: value.full_name.trim(),
        cookies: value.cookies === "accept-all",
        newsletter: value.newsletter,
        cadence: value.cadence,
      });
    },
    onSubmitInvalid: ({ value, formApi }) => {
      const validFields = rOmit(value, rKeys(rPickBy(formApi.state.fieldMeta, (field) => !field.isValid)) as (keyof typeof value)[]);

      updateProfileMutation.mutate({
        ...'full_name' in validFields ? { full_name: value.full_name.trim() } : {},
        ...'cookies' in validFields ? { cookies: value.cookies === "accept-all" } : {},
        ...'newsletter' in validFields ? { newsletter: value.newsletter } : {},
        ...'cadence' in validFields ? { cadence: value.cadence } : {},
      });
    },
  });

  const form = useForm({
    defaultValues: {
      health_care_provider: profileQuery.data?.health_care_provider,
      country_code: (profileQuery.data?.country_code ?? null) as Key | null,
      region_code: profileQuery.data?.region_code ?? null as Key | null,
      npi_number: String(profileQuery.data?.npi_number ?? ''),
      has_npi_number: profileQuery.data?.npi_number ? true : false,
      doctor_specialty: profileQuery.data?.doctor_specialty ?? null as Key | null,
      profession: profileQuery.data?.profession ?? '',
      isEditingPersonalInformation: false,
      locationSearch: allCountries.find((country) => country[1] === profileQuery.data?.country_code)?.[0] ?? '', // Default selected value needs to be in the list of countries to be displayed
    },
    canSubmitWhenInvalid: true,
    onSubmit: async ({ value, formApi }) => {
      const { error } = await minDelay(tryCatch(updateProfileMutation.mutateAsync({
        health_care_provider: value.health_care_provider,
        country_code: value.country_code!.toString(),
        region_code: value.region_code ? value.region_code.toString() : null,
        npi_number: value.has_npi_number ? (value.npi_number === '' ? null : Number(value.npi_number)) : null,
        profession: !value.health_care_provider && value.profession ? value.profession.trim() : null,
        doctor_specialty: value.health_care_provider && value.doctor_specialty
          ? value.doctor_specialty.toString()
          : null
      })), PROGRESS_INDICATOR_MIN_DELAY_DURATION);

      if (!error) {
        formApi.setFieldValue('isEditingPersonalInformation', false);
      } else {
        toastQueue.add({ message: 'Failed to update profile', variant: 'error' }, { timeout: TOAST_TIMEOUT });
      }
    },
  });

  const debouncedLocation = useDebounce(useStore(form.store, (state) => state.values.locationSearch), SKELETON_MIN_DELAY_DURATION);
  const locationsQuery = useQuery(locationQueries.get({ search: debouncedLocation }));

  const npiNumber = useStore(form.store, (state) => state.values.npi_number);
  const getSpecialtyByNpiQuery = useQuery(specialtyQuery.getByNpi({ npi_number: npiNumber }));

  const isDataRequestPending =
    requestDataMutation.data?.status === DataRequestStatus.NEW ||
    dataRequestDetailQuery.data?.status === DataRequestStatus.NEW;

  const isDataRequestFailed =
    requestDataMutation.data?.status === DataRequestStatus.ERROR ||
    dataRequestDetailQuery.data?.status === DataRequestStatus.ERROR;

  const isDataRequestDone =
    requestDataMutation.data?.status === DataRequestStatus.DONE ||
    dataRequestDetailQuery.data?.status === DataRequestStatus.DONE;

  const shouldDisableRequestDataButton = dataRequestDetailQuery.isLoading || isDataRequestPending || isDataRequestFailed || isDataRequestDone;

  return (
    <form {...stylex.props(formStyles.base)}>
      <section {...stylex.props(sectionStyles.field)}>
        <Heading level={2} size={20}>Account</Heading>
        <autoSaveForm.Field
          listeners={{ onBlur: () => autoSaveForm.handleSubmit() }}
          name="full_name"
          validators={{ onBlur: ProfileSchema.fullName }}>
          {(field) =>
            <TextField
              autoComplete="off"
              id={field.name}
              isInvalid={field.state.meta.errors.length > 0}
              name={field.name}
              size={48}
              style={fieldStyles.base}
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={field.handleChange}>
              <Label color={isMobile ? 'primary' : 'tertiary'}>Full name</Label>
              <Input />
              {field.state.meta.errors.length > 0 &&
                <Hint style={hintStyles.base}>
                  <Icon />
                  {field.state.meta.errors[0]?.message}
                </Hint>}
            </TextField>}
        </autoSaveForm.Field>

        <View style={fieldWithTextButtonStyles.base}>
          <TextField isDisabled size={48} style={fieldStyles.base} value={userQuery.data?.email}>
            <Label color={isMobile ? 'primary' : 'tertiary'}>Email</Label>
            <Input />
          </TextField>
          <LinkTextButton
            href="mailto:support@trendmd.com"
            size={32}
            style={fieldWithTextButtonStyles.textButton}
            variant="tertiary">
            Contact us to change
          </LinkTextButton>
        </View>

        <Group {...stylex.props(fieldsetStyles.base)} aria-labelledby={`${id}-password-label`}>
          <Text color={isMobile ? 'primary' : 'tertiary'} id={`${id}-password-label`} style={legendStyles.base}>Password</Text>
          <Group {...stylex.props(groupStyles.base, groupStyles.passwordButtons)}>
            <DialogTrigger>
              <Button isRounded size={40} variant="secondary">Change</Button>
              <ChangePasswordDialog />
            </DialogTrigger>
            <TextButton
              isDisabled={!userQuery.data?.email}
              safeArea="rounded"
              size={40}
              variant="secondary"
              onPress={() => recoverPasswordMutation.mutate(
                { email: userQuery.data?.email as string },
                { onSuccess: () => toastQueue.add({ message: 'Check your email for reset password instructions', variant: 'info' }, { timeout: TOAST_TIMEOUT }) })}>
              Reset
            </TextButton>
          </Group>
        </Group>
      </section>

      <section {...stylex.props(sectionStyles.field)}>
        <Heading level={2} size={20}>Personal</Heading>
        <form.Subscribe selector={(state) => state.values.isEditingPersonalInformation}>
          {(isEditingPersonalInformation) =>
            <>
              <form.Field name="health_care_provider">
                {(field) =>
                  <View style={fieldStyles.base}>
                    <Text color={isMobile ? 'primary' : 'tertiary'} id={`${id}-health-care-provider-label`} size={16}>Are you a HCP?</Text>
                    <SegmentedButtonGroup
                      aria-labelledby={`${id}-health-care-provider-label`}
                      isDisabled={!isEditingPersonalInformation}
                      selectedKeys={[field.state.value ? 'yes' : 'no']}
                      selectionMode="single"
                      size={40}
                      style={groupStyles.segmentedButton}
                      onSelectionChange={(keys) => {
                        field.handleChange(keys.keys().next().value === 'yes');
                        field.form.setFieldValue('doctor_specialty', null);
                        field.form.setFieldValue('profession', '');
                        field.form.setFieldValue('has_npi_number', false);
                        field.form.setFieldValue('npi_number', '');
                      }}>
                      <SegmentedButton id="yes" size={40}>Yes</SegmentedButton>
                      <SegmentedButton id="no" size={40}>No</SegmentedButton>
                    </SegmentedButtonGroup>
                  </View>}
              </form.Field>

              <form.Field name="country_code" validators={{ onSubmit: ProfileSchema.countryCode }}>
                {(field) => {
                  const isTouchedAndInvalid = field.getMeta().isTouched && field.state.meta.errors.length > 0;

                  return (
                    <ComboBox
                      allowsEmptyCollection
                      defaultFilter={() => true} // To return all regions if the country name is entered
                      id={field.name}
                      isDisabled={!isEditingPersonalInformation}
                      isInvalid={isTouchedAndInvalid}
                      name={field.name}
                      selectedKey={field.form.state.values.region_code ? `${field.state.value}-${field.form.state.values.region_code}` : field.state.value}
                      size={48}
                      style={fieldStyles.base}
                      onBlur={field.handleBlur}
                      onInputChange={(value) => {
                        // Only start searching for a location if the value has at least 3 characters, so that not too many results are rendered at once
                        if (value && value.length >= 3 && value !== field.state.value) {
                          field.form.setFieldValue('locationSearch', value);
                        }
                      }}
                      onSelectionChange={(key) => {
                        if (!key) {
                          return;
                        }

                        const [countryCode, regionCode] = key.toString().split('-');

                        if (field.state.value === 'US' || countryCode === 'US') {
                          field.form.setFieldValue('doctor_specialty', null);
                        }

                        if (countryCode !== 'US') {
                          field.form.setFieldValue('npi_number', '');
                          field.form.setFieldValue('has_npi_number', false);
                        }

                        field.form.setFieldValue('region_code', typeof regionCode === 'string' ? regionCode : null);

                        field.handleChange(countryCode);
                      }}>
                      {(renderProps) =>
                        <>
                          <Label color={isMobile ? 'primary' : 'tertiary'}>Location</Label>
                          <Input>
                            {(renderProps) => renderProps.defaultChildren}
                          </Input>

                          {isTouchedAndInvalid &&
                            <Hint style={hintStyles.base}>
                              <Icon />
                              {field.state.meta.errors[0]?.message}
                            </Hint>}
                          <Popover isOpen={renderProps.isOpen && !!locationsQuery.data?.length}>
                            <Virtualizer
                              layout={ListLayout}
                              layoutOptions={{
                                rowHeight: 40,
                                padding: 0,
                                dropIndicatorThickness: 0,
                                gap: 0,
                              }}>
                              <ListBox items={locationsQuery.data ?? []}>
                                {({ name, code, regionName, regionCode }) =>
                                  <ListBoxItem
                                    id={regionCode ? `${code}-${regionCode}` : code}
                                    size={40}
                                    textValue={`${regionName || name}`}>
                                    {regionName ?
                                      <Text slot={LIST_BOX_ITEM_SLOTS.LABEL}>
                                        {regionName}&nbsp;
                                        <Text color="quaternary" size={12}>{name}</Text>
                                      </Text> :
                                      <Text slot={LIST_BOX_ITEM_SLOTS.LABEL}>{name}</Text>}
                                  </ListBoxItem>}
                              </ListBox>
                            </Virtualizer>
                          </Popover>
                        </>}
                    </ComboBox>
                  );
                }}
              </form.Field>

              <form.Subscribe selector={(state) => [state.values.country_code, state.values.health_care_provider, state.values.has_npi_number] as const}>
                {([country_code, isHCP, hasNPINumber]) => country_code ? isHCP ?
                  <>
                    {country_code === 'US' &&
                      <>
                        <View style={fieldWithTextButtonStyles.base}>
                          <form.Field
                            name="npi_number"
                            validators={{
                              onChangeAsyncDebounceMs: 400,
                              onChangeAsync: async ({ value, fieldApi }) => {
                                if (!fieldApi.form.state.values.has_npi_number) {
                                  return;
                                }

                                if (!value) {
                                  return { message: 'Can\'t be empty' };
                                }

                                if (value.length !== 10) {
                                  return { message: 'Enter a valid NPI number' };
                                }

                                const { data } = await tryCatch(getSpecialtyByNpiQuery.refetch());

                                if (!data?.data) {
                                  return { message: 'Enter valid NPI number' };
                                }
                              },
                            }}>
                            {(field) =>
                              <TextField
                                autoComplete="off"
                                id={field.name}
                                isDisabled={!hasNPINumber || !isEditingPersonalInformation}
                                isInvalid={field.state.meta.errors.length > 0}
                                name={field.name}
                                size={48}
                                style={fieldStyles.base}
                                value={field.state.value}
                                onBlur={field.handleBlur}
                                onChange={field.handleChange}>
                                <Label color={isMobile ? 'primary' : 'tertiary'}>NPI number</Label>
                                <Input>
                                  {(renderProps) =>
                                    <>
                                      {renderProps.defaultChildren}
                                      {getSpecialtyByNpiQuery.isLoading && <ProgressIndicator />}
                                    </>}
                                </Input>
                                {field.state.meta.errors.length > 0 &&
                                  <Hint style={hintStyles.base}>
                                    <Icon />
                                    {field.state.meta.errors[0]?.message}
                                  </Hint>}
                              </TextField>}
                          </form.Field>

                          <form.Field name="has_npi_number">
                            {(field) =>
                              <TextButton
                                isDisabled={!isEditingPersonalInformation}
                                style={fieldWithTextButtonStyles.textButton}
                                variant="tertiary"
                                onPress={() => {
                                  field.handleChange(!field.state.value);
                                  field.form.setFieldValue('npi_number', '');
                                  field.form.setFieldMeta('npi_number', (meta) => ({ ...meta, isDirty: false, isTouched: false }));
                                  field.form.setFieldValue('doctor_specialty', null);
                                }}>
                                {field.state.value ? 'I don’t have an NPI number' : 'Use NPI number'}
                              </TextButton>}
                          </form.Field>
                        </View>
                        {getSpecialtyByNpiQuery.data &&
                          <TextField isDisabled style={fieldStyles.base} value={getSpecialtyByNpiQuery.data ?? ''}>
                            <Label color={isMobile ? 'primary' : 'tertiary'}>Specialty</Label>
                            <Input />
                          </TextField>}
                      </>}

                    {country_code && !(country_code === 'US' && hasNPINumber) &&
                      <form.Field name="doctor_specialty" validators={{
                        onSubmit: ({ value, fieldApi }) => {
                          if (!fieldApi.form.state.values.health_care_provider || (fieldApi.form.state.values.country_code === 'US' && fieldApi.form.state.values.has_npi_number)) {
                            return;
                          }

                          return vSafeParse(ProfileSchema.specialty, value).issues;
                        }
                      }}>
                        {(field) => {
                          return country_code === 'US' ?
                            <TextField
                              autoComplete="off"
                              id={field.name}
                              isDisabled={!isEditingPersonalInformation}
                              isInvalid={field.state.meta.errors.length > 0}
                              name={field.name}
                              style={fieldStyles.base}
                              type="text"
                              value={field.state.value?.toString()}
                              onBlur={field.handleBlur}
                              onChange={field.handleChange}>
                              <Label color={isMobile ? 'primary' : 'tertiary'}>Specialty</Label>
                              <Input />
                              {field.state.meta.errors.length > 0 &&
                                <Hint style={hintStyles.base}>
                                  <Icon />
                                  {field.state.meta.errors[0]?.message}
                                </Hint>}
                            </TextField> :
                            <ComboBox
                              id={field.name}
                              isDisabled={!isEditingPersonalInformation}
                              isInvalid={field.state.meta.errors.length > 0}
                              name={field.name}
                              selectedKey={field.state.value}
                              size={48}
                              style={fieldStyles.base}
                              onBlur={field.handleBlur}
                              onSelectionChange={field.handleChange}>
                              <Label color={isMobile ? 'primary' : 'tertiary'}>Specialty</Label>
                              <Input />
                              <Popover>
                                <ListBox items={Object.entries(NonUSDoctorSpecialtyEnum)}>
                                  {([key, value]) => <ListBoxItem id={key} textValue={value}>{value}</ListBoxItem>}
                                </ListBox>
                              </Popover>
                              {field.state.meta.errors.length > 0 &&
                                <Hint style={hintStyles.base}>
                                  <Icon />
                                  {field.state.meta.errors[0]?.message}
                                </Hint>}
                            </ComboBox>;
                        }}
                      </form.Field>}
                  </> :
                  <form.Field name="profession" validators={{
                    onSubmit: ({ value, fieldApi }) => {
                      if (fieldApi.form.state.values.health_care_provider) {
                        return;
                      }

                      return vSafeParse(ProfileSchema.profession, value).issues;
                    }
                  }}>
                    {(field) =>
                      <TextField
                        autoComplete="off"
                        id={field.name}
                        isDisabled={!isEditingPersonalInformation}
                        isInvalid={field.state.meta.errors.length > 0}
                        name={field.name}
                        style={fieldStyles.base}
                        type="text"
                        value={field.state.value?.toString()}
                        onBlur={field.handleBlur}
                        onChange={field.handleChange}>
                        <Label color={isMobile ? 'primary' : 'tertiary'}>Profession</Label>
                        <Input />
                        {field.state.meta.errors.length > 0 &&
                          <Hint style={hintStyles.base}>
                            <Icon />
                            {field.state.meta.errors[0]?.message}
                          </Hint>}
                      </TextField>}
                  </form.Field> : null
                }
              </form.Subscribe>

              <View style={fieldStyles.base}>
                {isEditingPersonalInformation ?
                  <Group {...stylex.props([groupStyles.base, groupStyles.formButtons])}>
                    <form.Subscribe selector={(state) => state.isSubmitting}>
                      {(isSubmitting) =>
                        <>
                          <Button
                            isPending={isSubmitting}
                            isRounded
                            size={40}
                            style={formStyles.button}
                            variant="primary"
                            onPress={() => form.handleSubmit()}>
                            Save
                          </Button>
                          <Button
                            isDisabled={isSubmitting}
                            isRounded
                            size={40}
                            style={formStyles.button}
                            variant="ghost"
                            onPress={() => form.reset()}>
                            Cancel
                          </Button>
                        </>}
                    </form.Subscribe>
                  </Group> :
                  <Button
                    isRounded
                    size={40}
                    style={formStyles.button}
                    variant="secondary"
                    onPress={() => form.setFieldValue('isEditingPersonalInformation', !isEditingPersonalInformation)}>
                    Edit
                  </Button>}
              </View>
            </>}
        </form.Subscribe>
      </section>

      <section {...stylex.props(sectionStyles.field)}>
        <Heading id={`${id}-interests-label`} level={2} size={20}>Interests</Heading>
        <Group {...stylex.props(fieldStyles.base)} aria-labelledby={`${id}-interests-label`}>
          <div {...stylex.props(tagStyles.container)}>
            {(interestsQuery.data?.length ?? 0) > 0 &&
              <div {...stylex.props(tagStyles.grid)}>
                {interestsQuery.data?.map((interest) =>
                  <Text
                    key={interest.id}
                    size={13}
                    style={tagStyles.base}>
                    {interest.name} ({interest.tree_number})
                  </Text>)}
              </div>}
            <DialogTrigger>
              <TextButton size={32} style={tagStyles.addButton}>
                <EditStrokeIcon slot={TEXT_BUTTON_SLOTS.LEFT_ICON} />
                <Text>Edit</Text>
              </TextButton>
              <InterestsDialog
                defaultValue={interestsQuery.data ?? []}
                isEdit
                onSubmit={(value) => updateUserInterestsMutation.mutateAsync({ interest_ids: value })}
              />
            </DialogTrigger>
          </div>
        </Group>
      </section>

      <section {...stylex.props(sectionStyles.segmentedButton)}>
        <Heading id={`${id}-theme-label`} level={2} size={20}>Theme</Heading>
        <SegmentedButtonGroup
          aria-labelledby={`${id}-theme-label`}
          selectedKeys={[theme.toString()]}
          selectionMode="single"
          size={40}
          // do not submit the form here, because it will be handled by the useSyncTheme hook
          onSelectionChange={(keys) => keys.size > 0 && setTheme(Number(keys.keys().next().value!))}>
          <SegmentedButton id={Theme.SYSTEM.toString()}>System</SegmentedButton>
          <SegmentedButton id={Theme.LIGHT.toString()}>Light</SegmentedButton>
          <SegmentedButton id={Theme.DARK.toString()}>Dark</SegmentedButton>
        </SegmentedButtonGroup>
      </section>

      <section {...stylex.props(sectionStyles.segmentedButton)}>
        <Heading level={2} size={20}>Newsletter</Heading>
        <div {...stylex.props(segmentedButtonStyles.container)}>
          <Paragraph id={`${id}-newsletter-label`} size={16}>Receive personalized email digest</Paragraph>
          <autoSaveForm.Subscribe selector={(state) => state.values.newsletter}>
            {(newsletter) =>
              <autoSaveForm.Field name="cadence">
                {(field) =>
                  <SegmentedButtonGroup
                    aria-labelledby={`${id}-newsletter-label`}
                    disallowEmptySelection
                    selectedKeys={[newsletter ? field.state.value.toString() : 'off']}
                    size={40}
                    onSelectionChange={(keys) => {
                      const value = keys.keys().next().value;

                      field.form.setFieldValue('newsletter', value !== 'off');
                      field.handleChange(Number(value));

                      field.form.handleSubmit();
                    }}>
                    <SegmentedButton id="off">Off</SegmentedButton>
                    <SegmentedButton id={NewsletterCadence.Value1.toString()}>Daily</SegmentedButton>
                    <SegmentedButton id={NewsletterCadence.Value2.toString()}>Weekly</SegmentedButton>
                    <SegmentedButton id={NewsletterCadence.Value3.toString()}>Monthly</SegmentedButton>
                  </SegmentedButtonGroup>}
              </autoSaveForm.Field>}
          </autoSaveForm.Subscribe>
        </div>
      </section>

      <section {...stylex.props(sectionStyles.regular)}>
        <Heading level={2} size={20}>Cookies preferences</Heading>
        <Paragraph size={16}>
          TrendMD and its partners use cookies to provide you with a better, safer, and faster service and to support our business.
          Some cookies are necessary to use our services, improve our services, and make sure they work properly.
          To learn more, read {' '}
          <RouterLink hash="privacy-policy" size={16} target="_blank" to="/privacy-and-terms/$">Privacy policy</RouterLink> and {' '}
          <RouterLink hash="terms-of-use" size={16} target="_blank" to="/privacy-and-terms/$">Terms of use</RouterLink>.
        </Paragraph>

        <autoSaveForm.Field name="cookies">
          {(field) =>
            <RadioGroup
              id={field.name}
              name={field.name}
              value={field.state.value}
              onChange={(value) => {
                field.handleChange(value);
                autoSaveForm.handleSubmit();
              }}
              {...stylex.props(groupStyles.base, groupStyles.radio)}>
              <Radio value="accept-all">Accept all cookies</Radio>
              <Radio value="refuse-non-essential">Refuse non-essential cookies</Radio>
            </RadioGroup>}
        </autoSaveForm.Field>
      </section>

      <section {...stylex.props(sectionStyles.regular)}>
        <Heading level={2} size={20}>Data & privacy</Heading>
        <Group {...stylex.props(groupStyles.base, groupStyles.textButton)}>
          <RouterTextButtonLink
            hash="opt-out"
            safeArea="none"
            size={48}
            target="_blank"
            to="/privacy-and-terms/$"
            variant="tertiary">
            TrendMD opt-out
          </RouterTextButtonLink>
          <RouterTextButtonLink
            hash="do-not-sell-my-personal-information"
            safeArea="none"
            size={48}
            target="_blank"
            to="/privacy-and-terms/$"
            variant="tertiary">
            Do not sell my personal information
          </RouterTextButtonLink>
          <div {...stylex.props(requestDataButtonStyles.container)}>
            <TextButton
              isDisabled={shouldDisableRequestDataButton}
              safeArea="none"
              size={48}
              variant="tertiary"
              onPress={() => requestDataMutation.mutate()}>
              Request an archive of your data
            </TextButton>
            {isDataRequestDone && <Text color="quaternary" size={13}>You can request a new one after 24 hours</Text>}
            {isDataRequestPending && <Text color="quaternary" size={13}>You’ll get it by email within 24 hours</Text>}
            {isDataRequestFailed &&
              <FieldErrorContext value={{
                isInvalid: true,
                validationErrors: [],
                validationDetails: {
                  badInput: false,
                  customError: false,
                  patternMismatch: false,
                  rangeOverflow: false,
                  rangeUnderflow: false,
                  stepMismatch: false,
                  tooLong: false,
                  tooShort: false,
                  typeMismatch: false,
                  valueMissing: false,
                  valid: false
                }
              }}>
                <Hint>We couldn’t generate your data archive.</Hint>
              </FieldErrorContext>}
          </div>
          <DialogTrigger>
            <TextButton safeArea="none" size={48} variant="tertiary">Delete your account</TextButton>
            <DeleteAccountDialog />
          </DialogTrigger>
        </Group>
      </section>
    </form >
  );
}

function ContextBarComponent() {
  return (
    <div {...stylex.props(titleStyles.base)}>
      <Heading level={1} size={24}>Settings</Heading>
    </div>
  );
}

const titleStyles = stylex.create({
  base: {
    alignItems: 'baseline',
    display: 'flex',
    flexDirection: 'row',
    gap: 8,
    marginInline: {
      default: 'auto',
      [breakpoints.small]: 0,
    },
    paddingBlock: 12,
    paddingInline: {
      default: 20,
      [breakpoints.large]: 0,
    },
    width: {
      default: 728,
      [breakpoints.small]: '100%',
    },
  },
});

const formStyles = stylex.create({
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: 80,
    maxWidth: {
      default: 728,
      [breakpoints.small]: '100%',
    },
    paddingInline: {
      default: 20,
      [breakpoints.large]: 0,
    },
    paddingTop: 48,
    width: '100%'
  },
  button: {
    gridColumn: '2',
    width: 'fit-content',
  }
});

const sectionStyles = stylex.create({
  regular: {
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
  },
  field: {
    display: 'grid',
    gridTemplateColumns: {
      default: 'minmax(124px, auto) 1fr',
      [breakpoints.small]: '1fr',
    },
    rowGap: 24,
  },
  segmentedButton: {
    alignItems: 'flex-start',
    alignSelf: 'stretch',
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
  },
});

const fieldStyles = stylex.create({
  base: {
    alignItems: 'baseline',
    columnGap: 20,
    display: 'grid',
    gridColumn: '1 / -1',
    gridTemplateColumns: 'subgrid',
    rowGap: 6,
  }
});

const fieldWithTextButtonStyles = stylex.create({
  base: {
    alignItems: 'baseline',
    columnGap: 20,
    display: 'grid',
    gridColumn: '1 / -1',
    gridTemplateColumns: 'subgrid',
    rowGap: 8,
  },
  textButton: {
    gridColumn: '2',
    width: 'fit-content',
  }
});

const fieldsetStyles = stylex.create({
  base: {
    alignItems: 'baseline',
    borderColor: 'transparent',
    borderStyle: 'none',
    borderWidth: 0,
    columnGap: 20,
    display: 'grid',
    gridColumn: '1 / -1',
    gridTemplateColumns: 'subgrid',
    margin: 0,
    minInlineSize: 'unset',
    padding: 0,
    rowGap: 16,
  },
});

const hintStyles = stylex.create({
  base: {
    gridColumn: '2',
  }
});

const legendStyles = stylex.create({
  base: {
    fontSize: 'inherit',
    fontWeight: 'inherit',
    lineHeight: 'inherit',
    margin: 0,
    padding: 0,
  }
});

const groupStyles = stylex.create({
  base: {
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
  },
  radio: {
    gap: 16,
  },
  textButton: {
    gap: 12,
  },
  passwordButtons: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 8,
  },
  segmentedButton: {
    width: 'fit-content'
  },
  formButtons: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 8,
    gridColumn: '2',
  },
});

const tagStyles = stylex.create({
  base: {
    alignItems: 'center',
    backgroundColor: {
      default: 'transparent',
      ':hover': $.buttonSurfaceQuaternaryHover,
    },
    borderColor: {
      default: $.border10,
      ':hover': 'transparent',
    },
    borderRadius: 999,
    borderStyle: 'solid',
    borderWidth: 1,
    color: {
      default: $.textSecondary,
      ':hover': $.textPrimary,
    },
    display: 'flex',
    gap: 5,
    minHeight: 32,
    minWidth: 52,
    paddingBlock: 9,
    paddingInline: 12,
    textAlign: 'center',
    textDecoration: 'none',
    transitionDuration: '0.2s',
    transitionProperty: 'color, background-color, border',
    transitionTimingFunction: 'easy-in-out',
  },
  container: {
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    justifyContent: 'center',
    paddingTop: {
      default: 0,
      [breakpoints.small]: 8,
    }
  },
  grid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
  },
  addButton: {
    justifySelf: 'left',
  },
});

const segmentedButtonStyles = stylex.create({
  container: {
    alignItems: 'flex-start',
    alignSelf: 'stretch',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
});

const requestDataButtonStyles = stylex.create({
  container: {
    alignItems: 'flex-start',
    alignSelf: 'stretch',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  }
});
